import { api } from '../../../lib/api';
import { Conversation, ConversationFeedback } from '../../../types';

export const CONVERSATION_PAGE_SIZE = 50;

// Map snake_case API response to camelCase frontend Conversation type
function mapConversation(raw: Record<string, any>): Conversation {
  return {
    id: raw.id,
    customerId: raw.customer_id ?? raw.customerId ?? '',
    customerName: raw.customer_name ?? raw.customerName ?? '',
    customerPhone: raw.customer_phone ?? raw.customerPhone ?? '',
    customerEmail: raw.customer_email ?? raw.customerEmail,
    customerAvatar: raw.customer_avatar ?? raw.customerAvatar,
    channel: raw.channel ?? 'whatsapp',
    storeId: raw.store_id ?? raw.storeId ?? '',
    state: raw.state ?? 'bot_active',
    assignedStaffId: raw.assigned_staff_id ?? raw.assignedStaffId,
    assignedStaffName: raw.assigned_staff_name ?? raw.assignedStaffName,
    lastMessage: raw.last_message ?? raw.lastMessage ?? '',
    lastMessageAt: raw.last_message_at ?? raw.lastMessageAt ?? raw.last_activity ?? raw.lastActivity,
    unreadCount: raw.unread_count ?? raw.unreadCount ?? 0,
    priority: raw.priority ?? 'normal',
    labels: raw.labels ?? [],
    sentiment: raw.sentiment,
    sentimentScore: raw.sentiment_score ?? raw.sentimentScore,
    category: raw.category,
    topicSummary: raw.topic_summary ?? raw.topicSummary ?? undefined,
    escalationReason: raw.escalation_reason ?? raw.escalationReason,
    lastCallStatus: raw.last_call_status ?? raw.lastCallStatus ?? undefined,
    latestCsatScore: raw.latest_csat_score ?? raw.latestCsatScore ?? undefined,
    whatsappNumberId: raw.whatsapp_number_id ?? raw.whatsappNumberId ?? undefined,
    whatsappNumberLabel: raw.whatsapp_number_label ?? raw.whatsappNumberLabel ?? undefined,
    createdAt: raw.created_at ?? raw.createdAt,
    lastActivity: raw.last_activity ?? raw.lastActivity ?? raw.created_at ?? raw.createdAt,
    resolvedAt: raw.resolved_at ?? raw.resolvedAt,
  };
}

function mapFeedback(raw: Record<string, any>): ConversationFeedback {
  return {
    id: raw.id,
    conversationId: raw.conversation_id ?? raw.conversationId,
    storeId: raw.store_id ?? raw.storeId,
    resolvedAtSnapshot: raw.resolved_at_snapshot ?? raw.resolvedAtSnapshot,
    resolvedByStaffId: raw.resolved_by_staff_id ?? raw.resolvedByStaffId,
    resolvedByAgent: raw.resolved_by_agent ?? raw.resolvedByAgent ?? false,
    triggeredAt: raw.triggered_at ?? raw.triggeredAt,
    respondedAt: raw.responded_at ?? raw.respondedAt,
    score: raw.score,
    status: raw.status,
    comment: raw.comment,
    disputed: raw.disputed ?? false,
  };
}

// --- Staff-initiated outbound conversations ---

export interface WhatsAppTemplate {
  name: string;
  language: string;
  status: string;
  category?: string;
  bodyText?: string;
  paramCount: number;
  /** Sample value for each {{n}} variable, from the approved template. */
  paramExamples?: string[];
}

export interface InitiateConversationInput {
  customerPhone: string;
  customerName?: string;
  message?: string;
  templateName?: string;
  templateLanguage?: string;
  templateParams?: string[];
}

export interface InitiateConversationResult {
  conversationId: string;
  messageId: string;
  sendMode: 'text' | 'template';
  withinWindow: boolean;
  conversationCreated: boolean;
}

export const conversationService = {
  async getConversations(filter?: string, skip = 0, limit = CONVERSATION_PAGE_SIZE): Promise<Conversation[]> {
    const data = await api.get<Record<string, any>[]>('/conversations', { params: { filter, skip, limit } });
    return data.map(mapConversation);
  },

  async getConversation(id: string): Promise<Conversation> {
    const data = await api.get<Record<string, any>>(`/conversations/${id}`);
    return mapConversation(data);
  },

  async assignConversation(id: string, staffId: string): Promise<void> {
    return api.post<void>(`/conversations/${id}/assign`, { staffId });
  },

  async resolveConversation(id: string): Promise<void> {
    return api.post<void>(`/conversations/${id}/resolve`);
  },

  async returnToBot(id: string): Promise<void> {
    return api.post<void>(`/conversations/${id}/return-to-bot`);
  },

  async updatePriority(id: string, priority: string): Promise<void> {
    return api.patch<void>(`/conversations/${id}/priority`, null, {
      params: { priority },
    });
  },

  async updateLabels(id: string, labels: string[]): Promise<void> {
    return api.patch<void>(`/conversations/${id}/labels`, null, {
      params: { labels },
    });
  },

  async getFeedback(id: string): Promise<ConversationFeedback[]> {
    const data = await api.get<Record<string, any>[]>(`/conversations/${id}/feedback`);
    return data.map(mapFeedback);
  },

  async disputeFeedback(feedbackId: string, disputed: boolean): Promise<ConversationFeedback> {
    const data = await api.post<Record<string, any>>(`/feedback/${feedbackId}/dispute`, { disputed });
    return mapFeedback(data);
  },

  // Fetch approved WhatsApp templates for the compose UI. Returns [] when the
  // store isn't wired for template listing (operator types a name by hand).
  async getWhatsAppTemplates(): Promise<WhatsAppTemplate[]> {
    const data = await api.get<Record<string, any>[]>('/whatsapp/templates');
    return data.map((t) => ({
      name: t.name,
      language: t.language,
      status: t.status,
      category: t.category ?? undefined,
      bodyText: t.body_text ?? t.bodyText ?? undefined,
      paramCount: t.param_count ?? t.paramCount ?? 0,
      paramExamples: t.param_examples ?? t.paramExamples ?? undefined,
    }));
  },

  async initiateConversation(
    input: InitiateConversationInput
  ): Promise<InitiateConversationResult> {
    const params = input.templateParams?.filter((p) => p.trim().length > 0);
    const data = await api.post<Record<string, any>>('/whatsapp/initiate', {
      customer_phone: input.customerPhone,
      customer_name: input.customerName || undefined,
      message: input.message || undefined,
      template_name: input.templateName || undefined,
      template_language: input.templateLanguage || undefined,
      template_params: params && params.length ? params : undefined,
    });
    return {
      conversationId: data.conversation_id,
      messageId: data.message_id,
      sendMode: data.send_mode,
      withinWindow: data.within_window,
      conversationCreated: data.conversation_created,
    };
  },
};
